import { ImageType } from '@d-id/types';
import { useMutation, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { createDvpAvatar } from '@/pages/avatar/PremiumPlusCreatePage/api';
import { useFlagExperiment } from '@/services/featureFlags';

import { toast } from '@/components/toast';
import { createExpressAvatar } from '@/pages/avatar/CreatePage/api';
import { VALIDATE_USER_CAPPING_QUERY_KEY } from '@/pages/avatar/hooks';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { GET_PRESENTERS_KEY, GET_PRESENTERS_KEY_TYPE } from '@/state/presenters/presenters';
import { AppRoutes, MixPanelEvent } from '@/utils/types';

import { AvatarType } from '../common';
import { useCreateAvatarStore } from '../common/context';
import premiumPlusCreateAvatarStore from '../PremiumPlusCreatePage/CreateAvatarStore';

export const useCreateExpressAvatar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { from } = location.state || {};
    const queryClient = useQueryClient();

    const createAvatarStore = useCreateAvatarStore();
    const mixpanel = useAnalytics();
    const { user } = useAuth();

    const mixpanelPayload = {
        from,
        type: createAvatarStore.type,
        method: createAvatarStore.footageMethod,
        plan: user?.planGroup,
        presenterId: createAvatarStore.id,
        fromDraft: !!createAvatarStore.id,
        isScript: createAvatarStore.usedFootageScript,
        script_language: createAvatarStore.footageScriptLanguage,
        consent_language: createAvatarStore.consentLanguage,
    };

    return useMutation(
        () => {
            mixpanel?.track('avatar-generate', { ...mixpanelPayload, event: MixPanelEvent.CLICK });
            return createExpressAvatar(createAvatarStore.payload);
        },
        {
            onSuccess: async ({ id }) => {
                await queryClient.invalidateQueries([GET_PRESENTERS_KEY, GET_PRESENTERS_KEY_TYPE(ImageType.Upload)], {
                    refetchInactive: true,
                });
                await queryClient.invalidateQueries(VALIDATE_USER_CAPPING_QUERY_KEY, {
                    refetchInactive: true,
                });
                mixpanel?.track('avatar-generate', { ...mixpanelPayload, id, event: MixPanelEvent.SUCCESS });
                navigate(AppRoutes.AvatarsCreateSuccess);
            },
            onError: error => {
                mixpanel?.track('avatar-generate', { ...mixpanelPayload, event: MixPanelEvent.ERROR });
                logger.error('Failed to create avatar', error);
                createAvatarStore.setTab(createAvatarStore.tab - 1);
                toast.error({
                    title: 'Failed to create avatar, please try again',
                });
            },
        }
    );
};

export const useCreatePremiumPlusAvatar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { from } = location.state || {};
    const queryClient = useQueryClient();

    const createAvatarStore = useCreateAvatarStore();
    const mixpanel = useAnalytics();
    const { user } = useAuth();
    const shortTrainDuration = useFlagExperiment('dvp_short_train_duration', 0);

    const mixpanelPayload = {
        from,
        type: createAvatarStore.type,
        method: createAvatarStore.footageMethod,
        plan: user?.planGroup,
        presenterId: createAvatarStore.id,
        fromDraft: !!createAvatarStore.id,
        isScript: createAvatarStore.usedFootageScript,
        script_language: createAvatarStore.footageScriptLanguage,
        consent_language: createAvatarStore.consentLanguage,
        idle_video: premiumPlusCreateAvatarStore.idleVideo,
    };

    return useMutation(
        () => {
            mixpanel?.track('avatar-generate', { ...mixpanelPayload, event: MixPanelEvent.CLICK });
            return createDvpAvatar({
                ...createAvatarStore.payload,
                idle_video: premiumPlusCreateAvatarStore.idleVideo,
                shortTrainDuration,
            });
        },
        {
            onSuccess: async ({ id }) => {
                await queryClient.invalidateQueries([GET_PRESENTERS_KEY, GET_PRESENTERS_KEY_TYPE(ImageType.Upload)], {
                    refetchInactive: true,
                });
                await queryClient.invalidateQueries(VALIDATE_USER_CAPPING_QUERY_KEY, {
                    refetchInactive: true,
                });
                mixpanel?.track('avatar-generate', { ...mixpanelPayload, id, event: MixPanelEvent.SUCCESS });
                navigate(AppRoutes.AvatarCreatePremiumPlusSuccessPage);
            },
            onError: error => {
                mixpanel?.track('avatar-generate', { ...mixpanelPayload, event: MixPanelEvent.ERROR });
                logger.error('Failed to create avatar', error);
                createAvatarStore.setTab(createAvatarStore.tab - 1);
                toast.error({
                    title: 'Failed to create avatar, please try again',
                });
            },
        }
    );
};

export const useCreateAvatar = (type: AvatarType) => {
    const createExpressAvatar = useCreateExpressAvatar();
    const createPremiumPlusAvatar = useCreatePremiumPlusAvatar();

    return type === AvatarType.Express ? createExpressAvatar : createPremiumPlusAvatar;
};
